import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import NodeStatus from "./NodeStatus";
import { TopologyProvider } from "./TopologyContext";
import { DataProvider } from "./DataContext";
import "./App.css";

// Configure axios to send cookies on every request.
// Cookie auth requires this on all API calls site-wide.
axios.defaults.withCredentials = true;

const AUTH_ME_URL = "/api/auth/me";
const AUTH_LOGIN_URL = "/api/auth/login";
const AUTH_LOGOUT_URL = "/api/auth/logout";

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState("");
  const [loading, setLoading] = useState(true);
  const [inputUser, setInputUser] = useState("");
  const [inputPass, setInputPass] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // On mount: ask the backend whether we already have a valid session cookie.
  // If yes, skip straight to the dashboard. If no, show the login form.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(AUTH_ME_URL);
        if (cancelled) return;
        if (res.data?.authenticated) {
          setCurrentUser(res.data.username || "");
          setAuthenticated(true);
        }
      } catch {
        // 401 or network error — render login screen
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const clearSession = useCallback(async () => {
    try {
      await axios.post(AUTH_LOGOUT_URL);
    } catch {
      // even if the server call fails, drop client-side state
    }
    setAuthenticated(false);
    setCurrentUser("");
    setInputUser("");
    setInputPass("");
    setLoginError("");
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setSubmitting(true);
    try {
      const res = await axios.post(AUTH_LOGIN_URL, {
        username: inputUser,
        password: inputPass,
      });
      // On success the server set an httpOnly cookie. We only need the username
      // back for the welcome banner.
      setCurrentUser(res.data?.username || inputUser);
      setAuthenticated(true);
      setInputUser("");
      setInputPass("");
    } catch (err) {
      // Don't leak which field was wrong (matches backend behavior)
      const status = err?.response?.status;
      if (status === 401) {
        setLoginError("Invalid credentials");
      } else if (status === 400) {
        setLoginError("Username and password required");
      } else {
        setLoginError("Login failed — check your connection");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading screen
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#111827',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner"></div>
          <h2 style={{
            marginTop: '1rem',
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#9ca3af',
          }}>
            Loading...
          </h2>
        </div>
      </div>
    );
  }

  // Login screen
  if (!authenticated) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h2>SyberJet HPC Login</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <input
                type="text"
                placeholder="Username"
                value={inputUser}
                onChange={(e) => setInputUser(e.target.value)}
                autoComplete="username"
                required
                disabled={submitting}
              />
              <input
                type="password"
                placeholder="Password"
                value={inputPass}
                onChange={(e) => setInputPass(e.target.value)}
                autoComplete="current-password"
                required
                disabled={submitting}
              />
              <button type="submit" disabled={submitting}>
                {submitting ? "Signing in…" : "Login"}
              </button>
            </div>
          </form>
          {loginError && (
            <div style={{
              marginTop: '1rem', textAlign: 'center',
              color: '#dc2626', fontSize: '0.875rem'
            }}>
              {loginError}
            </div>
          )}
          <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.875rem', color: '#666' }}>
            <p>For SyberJet Employees only</p>
          </div>
        </div>
      </div>
    );
  }

  // Main dashboard — authenticated
  return (
    <TopologyProvider>
      <DataProvider>
        <NodeStatus user={currentUser} onLogout={clearSession} />
      </DataProvider>
    </TopologyProvider>
  );
}

export default App;
